import { useMemo, useState } from 'react';
import { X, Search, Loader2, Plus } from 'lucide-react';
import { useGetPoliciesQuery } from '@/store/api/apiSlice';
import type { Policy } from '@/types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface PolicySelectProps {
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  required?: boolean;
}

/**
 * Multi-select of policy IDs. Populated from the existing authorization
 * `/policies` endpoint when available; also allows free typing of arbitrary
 * policy IDs (the backend scopes them at validation time).
 */
export function PolicySelect({ value, onChange, label = 'Policies', required = false }: PolicySelectProps) {
  // Reuses the existing policy-list endpoint from the authorization service.
  const { data, isLoading } = useGetPoliciesQuery({ page: 1, pageSize: 100 });
  const knownPolicies = useMemo(() => {
    const raw = (data as { data?: Policy[] } | undefined)?.data ?? [];
    return Array.isArray(raw) ? (raw as Policy[]) : [];
  }, [data]);

  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);

  const draftLower = draft.trim().toLowerCase();

  // Options: known policies matching the query, plus the raw typed ID.
  const suggestions = useMemo(() => {
    const matches = knownPolicies
      .filter((p) => !value.includes(p._id) && (!draftLower || p.name.toLowerCase().includes(draftLower)))
      .slice(0, 12);
    const typed = draft.trim() && !value.includes(draft.trim()) ? [draft.trim()] : [];
    return { matches, typed };
  }, [knownPolicies, value, draftLower, draft]);

  const add = (id: string) => {
    const clean = id.trim();
    if (!clean || value.includes(clean)) return;
    onChange([...value, clean]);
    setDraft('');
  };

  const remove = (id: string) => onChange(value.filter((v) => v !== id));

  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>

      {/* Selected policy tags */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5">
        {value.length === 0 && (
          <span className="text-xs text-muted-foreground">No policies selected</span>
        )}
        {value.map((id) => {
          const known = knownPolicies.find((p) => p._id === id);
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
            >
              {known?.name ?? id}
              <button
                type="button"
                onClick={() => remove(id)}
                aria-label={`Remove policy ${id}`}
                className="rounded-full text-muted-foreground hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
      </div>

      {/* Input + suggestions */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={isLoading ? 'Loading policies...' : 'Type a policy name or ID...'}
          className="pl-9"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(draft);
            }
          }}
        />
        {open && !isLoading && (
          <div className="absolute z-[100] mt-1 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
            {suggestions.matches.length > 0 && (
              <ul className="max-h-48 overflow-y-auto">
                {suggestions.matches.map((p) => (
                  <li key={p._id}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        add(p._id);
                      }}
                    >
                      <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium">{p.name}</span>
                      <span className="ml-auto truncate font-mono text-xs text-muted-foreground">
                        {p._id}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {suggestions.typed.length > 0 && (
              <button
                type="button"
                className="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(draft);
                }}
              >
                <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span>Use &ldquo;{suggestions.typed[0]}&rdquo; as a policy ID</span>
              </button>
            )}
            {suggestions.matches.length === 0 && suggestions.typed.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">No policies found</p>
            )}
          </div>
        )}
      </div>

      {/* Inline loading indicator */}
      {isLoading && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading policies…
        </div>
      )}
    </div>
  );
}